/**
 * Storefront analytics — the derived numbers.
 *
 * The `storefront_analytics_traffic` RPC returns raw counts and nothing else.
 * Every rate, ratio and comparison the dashboard shows is defined here, in
 * plain TypeScript, so the metric definitions are testable and identical
 * whichever backend serves the counts.
 *
 * Also home to the Tunisian city → governorate normalisation: checkout collects
 * `city` as free text, so "sfax", "SFAX ", "Sakiet Ezzit" and "Sfax Ville" all
 * arrive as separate rows and an orders-by-location report reads as noise until
 * they're folded together.
 */

// ---------------------------------------------------------------------------
// Comparisons
// ---------------------------------------------------------------------------

/**
 * Percentage change from `previous` to `current`, as a ratio (0.25 = +25%).
 *
 * Returns null when there is no baseline: going from 0 to 12 is not "+1200%",
 * it's a first data point, and printing a percentage there is worse than
 * printing nothing.
 */
export function deltaRatio(current: number, previous: number): number | null {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous <= 0) return null;
  return (current - previous) / previous;
}

/** `'up' | 'down' | 'flat'`, with a dead band so noise doesn't flash arrows. */
export function deltaDirection(ratio: number | null, deadBand = 0.005): 'up' | 'down' | 'flat' {
  if (ratio === null || Math.abs(ratio) <= deadBand) return 'flat';
  return ratio > 0 ? 'up' : 'down';
}

// ---------------------------------------------------------------------------
// Rates
// ---------------------------------------------------------------------------

/** Safe share of a whole, clamped to [0, 1]. Returns null when the whole is 0. */
export function shareOf(part: number, whole: number): number | null {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole <= 0) return null;
  return Math.min(1, Math.max(0, part / whole));
}

export interface FunnelCounts {
  visits: number;
  productViews: number;
  addToCart: number;
  beginCheckout: number;
  orders: number;
  delivered: number;
}

export interface FunnelStep {
  key: keyof FunnelCounts;
  value: number;
  /** Share kept from the step before — null for the first step. */
  stepRate: number | null;
  /** Share of the very first step, for the bar width. */
  overallRate: number;
}

/**
 * The funnel as steps, each carrying BOTH ratios.
 *
 * `stepRate` (share kept from the previous step) is the actionable number — it
 * says which single transition is leaking. `overallRate` is what the bar
 * length should encode, so the chart stays an honest picture of the drop-off
 * instead of five bars of similar length.
 */
export function funnelSteps(counts: FunnelCounts): FunnelStep[] {
  const order: (keyof FunnelCounts)[] = [
    'visits', 'productViews', 'addToCart', 'beginCheckout', 'orders', 'delivered',
  ];
  const first = counts.visits;
  let previous: number | null = null;
  return order.map((key) => {
    const value = Math.max(0, counts[key] ?? 0);
    const stepRate = previous === null ? null : shareOf(value, previous);
    previous = value;
    return {
      key,
      value,
      stepRate,
      overallRate: first > 0 ? Math.min(1, value / first) : 0,
    };
  });
}

/** Visits that converted into an order. */
export function conversionRate(orders: number, visits: number): number | null {
  return shareOf(orders, visits);
}

/**
 * Share of page-view sessions that saw exactly one page.
 *
 * Measured over `pageSessions`, not over visits: a session that predates
 * page-view tracking has no pages at all, and counting it as a bounce would
 * quietly report 100% for every window overlapping the rollout.
 */
export function bounceRate(bouncedSessions: number, pageSessions: number): number | null {
  return shareOf(bouncedSessions, pageSessions);
}

/** Average pages opened per session. Null when nothing was measured. */
export function pagesPerSession(pageViews: number, pageSessions: number): number | null {
  if (pageSessions <= 0) return null;
  return pageViews / pageSessions;
}

/** Average order value. */
export function averageOrderValue(revenue: number, orders: number): number | null {
  if (orders <= 0) return null;
  return revenue / orders;
}

/** Revenue per visit — what one more visitor is worth. */
export function revenuePerVisit(revenue: number, visits: number): number | null {
  if (visits <= 0) return null;
  return revenue / visits;
}

/** Share of product views that ended in the cart. */
export function addToCartRate(addToCart: number, productViews: number): number | null {
  return shareOf(addToCart, productViews);
}

/** Share of started checkouts that became an order. */
export function checkoutCompletionRate(orders: number, beginCheckout: number): number | null {
  return shareOf(orders, beginCheckout);
}

// ---------------------------------------------------------------------------
// Tunisian geography
// ---------------------------------------------------------------------------

/** The 24 governorates, in the spelling the reports display. */
export const TUNISIA_GOVERNORATES = [
  'Ariana', 'Béja', 'Ben Arous', 'Bizerte', 'Gabès', 'Gafsa', 'Jendouba',
  'Kairouan', 'Kasserine', 'Kébili', 'Le Kef', 'Mahdia', 'La Manouba',
  'Médenine', 'Monastir', 'Nabeul', 'Sfax', 'Sidi Bouzid', 'Siliana',
  'Sousse', 'Tataouine', 'Tozeur', 'Tunis', 'Zaghouan',
] as const;

export type TunisiaGovernorate = (typeof TUNISIA_GOVERNORATES)[number];

/** Strip accents, punctuation and case so free-text cities compare cleanly. */
export function normalizeCityName(city: string): string {
  return city
    .normalize('NFD')
    // Combining diacritical marks — "Béja" → "Beja", "Gabès" → "Gabes".
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    // Keep latin, digits and the Arabic block; everything else is a separator.
    .replace(/[^a-z0-9؀-ۿ]+/g, ' ')
    .trim();
}

/**
 * Delegation / town → governorate. Only the entries that a plain "does the
 * city name contain the governorate name" test gets wrong are listed; anything
 * that already carries its governorate's name (Sfax Ville, Sousse Riadh, …)
 * resolves without an entry.
 */
const CITY_TO_GOVERNORATE: Record<string, TunisiaGovernorate> = {
  // Grand Tunis
  'la marsa': 'Tunis', marsa: 'Tunis', carthage: 'Tunis', 'sidi bou said': 'Tunis',
  'le bardo': 'Tunis', bardo: 'Tunis', 'el menzah': 'Tunis', menzah: 'Tunis',
  lafayette: 'Tunis', lac: 'Tunis', 'les berges du lac': 'Tunis', 'bab bhar': 'Tunis',
  'la goulette': 'Tunis', goulette: 'Tunis', kram: 'Tunis', 'le kram': 'Tunis',
  ettadhamen: 'Ariana', 'cite ettadhamen': 'Ariana', raoued: 'Ariana',
  soukra: 'Ariana', 'la soukra': 'Ariana', 'sidi thabet': 'Ariana', mnihla: 'Ariana',
  'ghazela': 'Ariana', 'borj louzir': 'Ariana',
  rades: 'Ben Arous', 'hammam lif': 'Ben Arous', 'hammam chatt': 'Ben Arous',
  ezzahra: 'Ben Arous', 'el mourouj': 'Ben Arous', mourouj: 'Ben Arous',
  megrine: 'Ben Arous', mohamedia: 'Ben Arous', fouchana: 'Ben Arous',
  'bou mhel': 'Ben Arous', mornag: 'Ben Arous', 'nouvelle medina': 'Ben Arous',
  'douar hicher': 'La Manouba', 'oued ellil': 'La Manouba',
  'den den': 'La Manouba', tebourba: 'La Manouba', 'borj el amri': 'La Manouba',
  // Nabeul / Cap Bon
  hammamet: 'Nabeul', kelibia: 'Nabeul', korba: 'Nabeul',
  'menzel temime': 'Nabeul', 'beni khiar': 'Nabeul', 'dar chaabane': 'Nabeul',
  soliman: 'Nabeul', grombalia: 'Nabeul',
  'el haouaria': 'Nabeul', haouaria: 'Nabeul', bouargoub: 'Nabeul', takelsa: 'Nabeul',
  // Sousse / Monastir / Mahdia
  'hammam sousse': 'Sousse', 'kalaa kebira': 'Sousse',
  'kalaa seghira': 'Sousse', msaken: 'Sousse', 'akouda': 'Sousse',
  'port el kantaoui': 'Sousse', enfidha: 'Sousse', bouficha: 'Sousse',
  'sidi bou ali': 'Sousse', hergla: 'Sousse',
  'ksar hellal': 'Monastir', moknine: 'Monastir', jemmal: 'Monastir',
  'sahline': 'Monastir', teboulba: 'Monastir', bembla: 'Monastir',
  'ksibet el mediouni': 'Monastir', 'sayada': 'Monastir', 'bekalta': 'Monastir',
  'ksour essef': 'Mahdia', chebba: 'Mahdia', 'el jem': 'Mahdia', jem: 'Mahdia',
  melloulech: 'Mahdia', 'sidi alouane': 'Mahdia', 'boumerdes': 'Mahdia',
  // Sfax
  'sakiet ezzit': 'Sfax', 'sakiet eddaier': 'Sfax', 'sakiet ed daier': 'Sfax',
  'el ain': 'Sfax', gremda: 'Sfax', 'chihia': 'Sfax', thyna: 'Sfax',
  'mahres': 'Sfax', 'jebeniana': 'Sfax', 'el hencha': 'Sfax', 'agareb': 'Sfax',
  'bir ali ben khelifa': 'Sfax', kerkennah: 'Sfax', 'skhira': 'Sfax',
  // Nord-Ouest
  'menzel bourguiba': 'Bizerte', mateur: 'Bizerte', 'ras jebel': 'Bizerte',
  sejnane: 'Bizerte', 'el alia': 'Bizerte', tinja: 'Bizerte', 'testour': 'Béja', 'medjez el bab': 'Béja', 'nefza': 'Béja', teboursouk: 'Béja',
  'tabarka': 'Jendouba', 'ain draham': 'Jendouba', bousalem: 'Jendouba',
  'fernana': 'Jendouba', 'ghardimaou': 'Jendouba',
  'dahmani': 'Le Kef', tajerouine: 'Le Kef', 'sers': 'Le Kef', kef: 'Le Kef',
  'makthar': 'Siliana', 'bou arada': 'Siliana', gaafour: 'Siliana', kesra: 'Siliana',
  // Centre
  'haffouz': 'Kairouan', 'sbikha': 'Kairouan', 'oueslatia': 'Kairouan',
  'hajeb el ayoun': 'Kairouan', chebika: 'Kairouan',
  'sbeitla': 'Kasserine', 'feriana': 'Kasserine', 'thala': 'Kasserine',
  'foussana': 'Kasserine', 'sbiba': 'Kasserine',
  'regueb': 'Sidi Bouzid', 'meknassy': 'Sidi Bouzid', 'jelma': 'Sidi Bouzid',
  'bir el hafey': 'Sidi Bouzid', 'menzel bouzaiene': 'Sidi Bouzid',
  'zaghouan ville': 'Zaghouan', 'el fahs': 'Zaghouan', fahs: 'Zaghouan',
  'bir mcherga': 'Zaghouan', nadhour: 'Zaghouan',
  // Sud
  'metouia': 'Gabès', mareth: 'Gabès', 'el hamma': 'Gabès', ghannouch: 'Gabès',
  'matmata': 'Gabès', 'zarat': 'Gabès',
  'metlaoui': 'Gafsa', 'redeyef': 'Gafsa', 'moulares': 'Gafsa', 'sened': 'Gafsa',
  'mdhilla': 'Gafsa', 'oum el araies': 'Gafsa',
  'djerba': 'Médenine', jerba: 'Médenine', 'houmt souk': 'Médenine', midoun: 'Médenine', ajim: 'Médenine',
  'zarzis': 'Médenine', 'ben gardane': 'Médenine', 'beni khedache': 'Médenine',
  'nefta': 'Tozeur', 'degache': 'Tozeur', 'tameghza': 'Tozeur', hazoua: 'Tozeur',
  'douz': 'Kébili', 'souk lahad': 'Kébili', 'el faouar': 'Kébili',
  'ghomrassen': 'Tataouine', 'remada': 'Tataouine', 'dehiba': 'Tataouine',
  'bir lahmar': 'Tataouine', 'smar': 'Tataouine',
};

/** Governorate names pre-normalised once, longest first so "Ben Arous" wins
 *  over a substring match on a shorter name. */
const NORMALIZED_GOVERNORATES: { normalized: string; name: TunisiaGovernorate }[] =
  TUNISIA_GOVERNORATES
    .map((name) => ({ normalized: normalizeCityName(name), name }))
    .sort((a, b) => b.normalized.length - a.normalized.length);

/**
 * Best-effort governorate for a free-text city. Returns null when nothing
 * matches — the caller shows those under an "other" bucket rather than
 * inventing a location.
 */
export function cityToGovernorate(city: string | null | undefined): TunisiaGovernorate | null {
  if (!city) return null;
  const normalized = normalizeCityName(city);
  if (!normalized) return null;

  const direct = CITY_TO_GOVERNORATE[normalized];
  if (direct) return direct;

  // "sfax ville", "grand tunis", "sousse ville" — the governorate's own name
  // appears inside the string.
  for (const g of NORMALIZED_GOVERNORATES) {
    if (normalized === g.normalized) return g.name;
  }
  for (const g of NORMALIZED_GOVERNORATES) {
    if (new RegExp(`(^| )${g.normalized}( |$)`).test(normalized)) return g.name;
  }
  // Last resort: a known delegation appearing as part of a longer string
  // ("cite ettadhamen ariana", "route de gremda km 6").
  for (const [town, gov] of Object.entries(CITY_TO_GOVERNORATE)) {
    const t = normalizeCityName(town);
    if (t.length >= 4 && new RegExp(`(^| )${t}( |$)`).test(normalized)) return gov;
  }
  return null;
}

export interface CityAmount {
  city: string | null | undefined;
  orders: number;
  revenue: number;
}

export interface GovernorateAmount {
  governorate: string;
  orders: number;
  revenue: number;
}

/**
 * Fold per-city rows into governorates, sorted by revenue. Cities that don't
 * resolve are grouped under `unresolvedLabel` so the totals still add up —
 * silently dropping them would make the report disagree with the KPI card.
 */
export function rollupByGovernorate(
  rows: readonly CityAmount[],
  unresolvedLabel = 'Autre',
): GovernorateAmount[] {
  const out = new Map<string, GovernorateAmount>();
  for (const row of rows) {
    const key = cityToGovernorate(row.city) ?? unresolvedLabel;
    const current = out.get(key) ?? { governorate: key, orders: 0, revenue: 0 };
    current.orders += row.orders;
    current.revenue += row.revenue;
    out.set(key, current);
  }
  return Array.from(out.values()).sort((a, b) => b.revenue - a.revenue);
}
